import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Modal from '@decisiv/ui-components/lib/components/Modal';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import Button from '@decisiv/ui-components/lib/components/Button';
import RadioGroup from '@decisiv/ui-components/lib/components/RadioGroup';
import { Alert, Grid, P } from '@decisiv/ui-components';

import iconx from '@decisiv/iconix';

import _, { delay } from 'lodash';

import { t, Trans } from '@lingui/macro';

import noop from 'lodash/noop';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';

import fileToEncoded from '../../../utils/fileToEncoded';
import BulkSyncSummary from '../../../components/BulkSyncSummary';
import WrapperWithLoading from '../../../components/WrapperWithLoading';
import { GreyishFlex } from '../../../components/StyledComponents';
import IntegrationsApi from '../../../api/integrations';
import IntegrationImportLocationsApi from '../../../api/integration_import_locations';
import SrmAccountsApi from '../../../api/srm_accounts';
import LocationsResult from './LocationsResult';
import AddMultipleLocations from './AddMultipleLocations';
import { HelpInputMessage, HelpInputText } from './styles';
import cleanImportLocationNotification from '../../../utils/cleanImportLocationNotification';
import setImportNotification from '../../../utils/setImportNotification';
import localCache from '../../../utils/localCache';
import { STORAGE_KEYS } from '../../../utils/storageKeys';
import { formatTimestamp } from '../../../utils/dates';

// An import is still moving while its rows are being written and while they are
// being propagated to the linked integrations.
const RUNNING_IMPORT_STATUSES = ['triggered', 'processing'];
const POLL_INTERVAL_MS = 5000;
// ~20 minutes. A safety net against polling forever, not an expected duration:
// propagation runs in chunks, so a large file legitimately takes several minutes.
const MAX_POLL_ATTEMPTS = 240;

export default function AddLocationModal({
  integration = {},
  setRefresh = noop,
  ...props
}) {
  const navigate = useNavigate();
  const [filterSearch, setFilterSearch] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [multipleLocationsMode, setMultipleLocationsMode] = useState(false);
  const [readyToSearch, setReadyToSearch] = useState(filterSearch !== '');
  const [locations, setLocations] = useState([]);
  const [totalLocations, settotalLocations] = useState(0);
  const [selectedLocationId, setSelectedLocationId] = useState(0);
  const inputRef = useRef(null);
  const [fileToUpload, setFileToUpload] = useState({});
  const [postingLocations, setPostingLocations] = useState(false);
  const [addLocations, setAddLocations] = useState(false);
  const [invalidFileMessage, setInvalidFileMessage] = useState('');
  const [uploadReportFile, setUploadReportFile] = useState('');
  const [uploadEndedDate, setUploadEndedDate] = useState('');
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [importerDetails, setImporterDetails] = useState('');
  const [syncSummary, setSyncSummary] = useState(null);
  const [pollGaveUp, setPollGaveUp] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const pollTimeoutRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  // The import this chain is following, held from the moment its request goes out
  // until the chain ends. A boolean could not tell "someone is already on this
  // import" from "someone is on a stale one", which is what reopening has to know.
  const pollOwnerRef = useRef(null);
  const wasVisibleRef = useRef(props.visibleModal);
  // The import whose result is on screen, so a chain that takes over a different one
  // can clear it instead of reporting the old file's outcome against the new one.
  const shownImportRef = useRef(null);
  const mountedRef = useRef(true);
  // Holds the poll body so the scheduler can reach it without the two of them
  // referring to each other before either exists.
  const pollRef = useRef(null);
  const { notify } = useNotifications();

  const notificationSuccessProps = {
    intent: 'success',
    title: t`Upload Completed`,
    duration: 3500,
    onClose: noop,
  };

  const notificationErrorProps = {
    intent: 'danger',
    title: t`Upload Failed`,
    duration: 3500,
    onClose: noop,
  };

  const search = (e) => {
    e.preventDefault();

    setReadyToSearch(true);
    setLoading(true);

    async function getSrmAccounts() {
      const response = await SrmAccountsApi.fetchSrmAccounts(filterSearch);

      if (response.data) {
        setLocations(response.data);
        settotalLocations(response.totalRecords);
      } else {
        setLocations([]);
      }
      setLoading(false);
    }

    getSrmAccounts();
  };

  const submitIntegration = async () => {
    setShowAlert(false);

    const schema = integration.attributes.location_settings_schema;

    const locationSettingsAttributes = _.chain(schema).map((attribute) => [
      attribute.name,
      attribute.type,
    ]);

    const defaultLocationSettingsAttributes = [];

    locationSettingsAttributes.value().map((element) => {
      let defaultValue = '';
      switch (element[1]) {
        case 'array':
          defaultValue = [];
          break;
        case 'object':
          defaultValue = {};
          break;
        default:
          defaultValue = '';
      }

      defaultLocationSettingsAttributes.push({
        name: element[0],
        value: defaultValue,
      });

      return defaultLocationSettingsAttributes;
    });

    const jsonBody = {
      srm_account_id: selectedLocationId,
      integration_uuid: integration.id,
      location_settings_attributes: defaultLocationSettingsAttributes,
    };

    const response = await IntegrationsApi.addIntegrationToLocation(jsonBody);

    /* istanbul ignore else */
    if (response.data) {
      const path = `/location_settings/${response.data.id}`;
      const sharedSettingSync = response.meta?.shared_setting_sync;

      // The cascade summary rides along so the settings page can report which
      // linked integrations also received the location — this modal unmounts.
      if (sharedSettingSync) {
        navigate(path, { state: { sharedSettingSync } });
      } else {
        navigate(path);
      }
    } else {
      setShowAlert(true);
    }
  };

  const belongsHere = () =>
    localCache.get(STORAGE_KEYS.IMPORT_LOCATION_INTEGRATION_ID) ===
    integration.id;

  const clearReportedResult = () => {
    shownImportRef.current = null;
    setUploadReportFile('');
    setUploadEndedDate('');
    setImporterDetails('');
    setSyncSummary(null);
    setUploadFailed(false);
  };

  // A chain taking an import over starts with the attempts that import is owed, not
  // with what the one before it had spent, and with nothing of the previous import's
  // result left on screen to stand in for it.
  const takeOver = (importId) => {
    if (pollOwnerRef.current !== importId) {
      pollAttemptsRef.current = 0;
      if (shownImportRef.current && shownImportRef.current !== importId)
        clearReportedResult();
    }
    pollOwnerRef.current = importId;
  };

  const releasePoll = (importLocationId) => {
    if (pollOwnerRef.current === importLocationId) pollOwnerRef.current = null;
  };

  // Every reschedule goes through here, so the timer is always the one the unmount
  // cleanup clears, and an answer arriving after the modal is gone cannot start a
  // new one.
  const schedulePoll = () => {
    if (!mountedRef.current) return;

    // Stopping quietly would leave "Upload in Progress..." on screen for good,
    // with no way to reach the report short of a reload.
    if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
      pollOwnerRef.current = null;
      setUploadInProgress(false);
      setPollGaveUp(true);
      return;
    }

    pollAttemptsRef.current += 1;
    // One timer at a time, so a new import starting over a pending poll cannot
    // leave a chain behind that the unmount cleanup no longer knows about.
    clearTimeout(pollTimeoutRef.current);
    pollTimeoutRef.current = delay(
      () => pollRef.current?.(true),
      POLL_INTERVAL_MS,
    );
  };

  const fetchImportInformation = async (forceFetch = false) => {
    const importLocationId = localCache.get(STORAGE_KEYS.IMPORT_LOCATION_ID);
    const importLocationIntegrationId = localCache.get(
      STORAGE_KEYS.IMPORT_LOCATION_INTEGRATION_ID,
    );
    const shouldDisplayNotification = localCache.get(
      STORAGE_KEYS.SHOULD_DISPLAY_NOTIFICATION,
    );

    // A tracked import for this integration is the entry price either way: the
    // storage keys are global, and a poll firing after they changed would otherwise
    // ask for `null` or adopt another integration's import. Forcing only gets past
    // the report already on screen.
    const tracked =
      Boolean(importLocationId) &&
      importLocationIntegrationId === integration.id;

    if (!tracked) {
      // A poll can fire after the tracked import moved to another integration or
      // was removed: nothing of this integration's is running any more, and no
      // timer is left to say so.
      if (pollOwnerRef.current) {
        pollOwnerRef.current = null;
        setUploadInProgress(false);
      }
      return;
    }

    if (uploadReportFile === '' || forceFetch) {
      takeOver(importLocationId);
      const response = await IntegrationImportLocationsApi.fetchImportLocation(
        importLocationId,
      );

      // The page can be gone by the time this answers, and a toast about a file the
      // user has walked away from is worse than no toast.
      if (!mountedRef.current) return;

      // Another import can be started while this request is out. Its answer would
      // report the wrong file and clear the new one's notification with it, so this
      // chain drops it — and then takes the replacement over, because an import
      // started elsewhere has no chain of its own to pick it up.
      const trackedNow = localCache.get(STORAGE_KEYS.IMPORT_LOCATION_ID);
      if (trackedNow !== importLocationId) {
        releasePoll(importLocationId);
        if (trackedNow && belongsHere()) {
          if (!pollOwnerRef.current) {
            // Claimed before the timer, not when it fires: reopening the modal in
            // between would otherwise start a chain of its own alongside this one.
            takeOver(trackedNow);
            schedulePoll();
          }
        } else {
          // Replaced by another integration's import, or untracked: nothing of this
          // integration's is running any more, and no timer is left to say so.
          setUploadInProgress(false);
        }
        return;
      }

      // A failed status request says nothing about the import, which is still
      // running server-side — keep asking instead of reporting nothing forever, and
      // say so on screen while it retries.
      if (!response.data) {
        setUploadInProgress(true);
        schedulePoll();
        return;
      }

      const importer = response.data.attributes;

      // `processing` covers propagating the rows across the Shared Setting, which
      // takes as many runs as the file needs — the report is only final after it.
      if (RUNNING_IMPORT_STATUSES.includes(importer.status)) {
        setUploadReportFile('');
        setUploadEndedDate('');
        setUploadInProgress(true);
        schedulePoll();
        return;
      }

      releasePoll(importLocationId);
      shownImportRef.current = importLocationId;
      const failed = importer.status === 'failed';

      setUploadReportFile(failed ? '' : importer.path_result_file);
      setUploadEndedDate(importer.updated_at);
      setImporterDetails(importer.details);
      setSyncSummary(importer.sync_summary);
      setUploadInProgress(false);
      setUploadFailed(failed);

      if (shouldDisplayNotification === true) {
        notify(
          failed ? notificationErrorProps : notificationSuccessProps,
          failed
            ? t`Something went wrong when processing your file, check your CSV file format and try again.`
            : t`We finished processing your file, open the add location modal to download the report.`,
        );
        localCache.set(STORAGE_KEYS.SHOULD_DISPLAY_NOTIFICATION, false);
      }
    }
  };

  pollRef.current = fetchImportInformation;

  const submitMultipleLocations = async () => {
    setPostingLocations(true);
    setUploadFailed(false);
    setUploadReportFile('');
    setSyncSummary(null);
    setPollGaveUp(false);
    cleanImportLocationNotification();
    // Convert uploaded CSV File to Text
    const encodedCsv = await fileToEncoded(fileToUpload);

    const response = await IntegrationImportLocationsApi.createImportLocation(
      integration.id,
      encodedCsv,
    );

    setImportNotification(response.data.id, integration.id);
    pollOwnerRef.current = response.data.id;
    setUploadInProgress(true);
    setPostingLocations(false);
    pollAttemptsRef.current = 0;
    schedulePoll();
  };

  const resetModalState = () => {
    setFileToUpload({});
    setInvalidFileMessage('');
    inputRef.current.value = '';
    setSelectedLocationId('');
    setFilterSearch('');
    setReadyToSearch(false);
    setLocations([]);
    setShowAlert(false);
    setPostingLocations(false);
    setAddLocations(false);
    setUploadReportFile('');
    setUploadEndedDate('');
    setUploadFailed(false);
  };

  const handleLocationsMode = () => {
    setMultipleLocationsMode(!multipleLocationsMode);
  };

  const handleCloseModal = () => {
    props.setVisibleModal(false);
    setMultipleLocationsMode(false);
    resetModalState();
  };

  const handleAddLocation = () => {
    if (multipleLocationsMode) {
      handleCloseModal();
      setAddLocations(true);
      submitMultipleLocations();
    } else {
      submitIntegration();
    }
  };

  const handleDownloadTemplate = async () => {
    const response = await IntegrationsApi.fetchTemplateFile(integration.id);

    /* istanbul ignore else */
    if (response) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${integration.attributes.name
          .replace(/\s+/g, '-')
          .toLowerCase()}-bulk-enabling.csv`,
      );
      document.body.appendChild(link);
      link.click();
    }
  };

  const handleRemovalOfSelectedFile = () => {
    setFileToUpload({});
    setInvalidFileMessage('');
    inputRef.current.value = '';
  };

  const handleBrowseFile = () => {
    inputRef.current.click();
  };

  const handleFileChange = (e) => {
    e.preventDefault();
    const maxFileSizeInMb = 5;
    const maxFileSize = 1024 * 1024 * maxFileSizeInMb;
    const file = e.target.files && e.target.files[0];

    if (!file) {
      return;
    }

    if (file.size > maxFileSize) {
      setInvalidFileMessage(
        t`${file.name} exceeds the max file size of ${maxFileSizeInMb}Mb.`,
      );
      return;
    }

    if (file.type !== 'text/csv') {
      setInvalidFileMessage(t`The file format for ${file.name} is invalid.`);
      return;
    }

    setFileToUpload(file);
  };

  const isFilePresent = useMemo(
    () => fileToUpload && fileToUpload.name,
    [fileToUpload],
  );

  const locationLegend = useMemo(
    () => (multipleLocationsMode ? 'locations' : 'location'),
    [multipleLocationsMode],
  );

  const trashWithLegend = () => (
    <Flex alignItems="center">
      <iconx.Trash />
      <P marginLeft={1}>{t`REMOVE`}</P>
    </Flex>
  );

  // Mounted once per integration (the page keys this modal by it), so the tracked
  // import is looked up here and never has to be reset for a route change.
  useEffect(() => {
    fetchImportInformation();

    // The poll reschedules itself, so without this it outlives the page.
    return () => {
      mountedRef.current = false;
      clearTimeout(pollTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The page keeps this modal mounted, so reopening it never remounts. A poll that
  // ran out of attempts while it was closed is picked back up here; one still
  // running is left alone rather than doubled, and the mount fetch above already
  // covers a modal that was open from the start.
  useEffect(() => {
    const justOpened = props.visibleModal && !wasVisibleRef.current;
    wasVisibleRef.current = props.visibleModal;
    const tracked = localCache.get(STORAGE_KEYS.IMPORT_LOCATION_ID);
    if (!justOpened || pollOwnerRef.current === tracked) return;

    pollAttemptsRef.current = 0;
    setPollGaveUp(false);
    // Forced when this integration has an import on record: a report still on screen
    // from the last one would otherwise keep the new one from ever being fetched.
    fetchImportInformation(
      Boolean(tracked) &&
        localCache.get(STORAGE_KEYS.IMPORT_LOCATION_INTEGRATION_ID) ===
          integration.id,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visibleModal]);

  return (
    <>
      <Modal
        actions={[
          {
            text: multipleLocationsMode ? t`Import Locations` : t`Add Location`,
            onClick: handleAddLocation,
            disabled:
              (selectedLocationId === '' || selectedLocationId === 0) &&
              !isFilePresent,
          },
          {
            text: t`Cancel`,
            onClick: handleCloseModal,
          },
        ]}
        icon={iconx.PlusCircle}
        color="information"
        onClose={handleCloseModal}
        title={t`Add Locations`}
        visible={props.visibleModal}
      >
        <Flex flexDirection="column">
          {uploadReportFile && (
            <Flex flexDirection="column">
              <Alert
                intent="success"
                dismissable
                marginBottom={1}
                title={t`Sucessfully Processed File`}
              >
                Download the CSV report file generated on{' '}
                <b>{formatTimestamp(uploadEndedDate)} </b>
                to check for any errors on your upload. The report contains the
                following information: <br />
                <br />
                {importerDetails}
              </Alert>
              <Button
                text="Download Report"
                icon={iconx.Download}
                style={{ alignSelf: 'flex-start' }}
                onClick={() => window.open(uploadReportFile, '_blank')}
              />
              {syncSummary && <BulkSyncSummary summary={syncSummary} />}
            </Flex>
          )}

          {uploadInProgress && (
            <Alert
              intent="information"
              dismissable
              title={t`Upload in Progress...`}
            >
              <Trans>
                There is currently an upload in progress for this integration.
                Please wait for it to complete before uploading another file.
              </Trans>
            </Alert>
          )}

          {pollGaveUp && (
            <Alert intent="warning" dismissable title={t`Still Processing`}>
              <Trans>
                This import is taking longer than expected. It is still running
                — reopen this window later to download the report.
              </Trans>
            </Alert>
          )}

          {uploadFailed && (
            <Alert intent="danger" dismissable title={t`Upload Failed`}>
              <Trans>
                Something went wrong when processing your file, check your file
                for errors and try again. In case the problem persists, please
                contact support. Error Message: {importerDetails}
              </Trans>
            </Alert>
          )}
          <P shade={1} marginTop={1}>
            <Trans>Choose an option to add locations.</Trans>
          </P>
          {showAlert ? (
            <Alert
              marginTop={1}
              title="Error"
              description={t`There was an error adding the integration to the ${locationLegend}`}
              intent="danger"
            />
          ) : null}

          {!isFilePresent && (
            <Grid.Row marginTop={1.7} marginBottom={2}>
              <Grid.Column size="MD" span="10">
                <RadioGroup
                  name="radio2"
                  size="normal"
                  items={[
                    { label: t`Single Location`, value: 1 },
                    { label: t`Multiple Locations`, value: 2 },
                  ]}
                  defaultValue={multipleLocationsMode ? 2 : 1}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onChange={handleLocationsMode}
                />
              </Grid.Column>
            </Grid.Row>
          )}

          <Grid.Row>
            {!multipleLocationsMode ? (
              <>
                <Flex marginLeft={1} marginBottom={1}>
                  <P shade={1}>
                    <Trans>Add one location to this integration.</Trans>
                  </P>
                </Flex>

                <Grid.Column size="MD" span="10">
                  <TextField
                    icon={iconx.Search}
                    label="searchInput"
                    hideLabel
                    name="input-ex-1"
                    onChange={(e) => {
                      setReadyToSearch(false);
                      setSelectedLocationId('');
                      setFilterSearch(e.target.value);
                    }}
                    value={filterSearch}
                  />

                  <HelpInputMessage alignItems="center">
                    <iconx.ExclamationCircleF />
                    <HelpInputText shade={1}>
                      <Trans>Search by Decisiv Dealer or SRM Account ID</Trans>
                    </HelpInputText>
                  </HelpInputMessage>
                </Grid.Column>

                <Grid.Column size="MD" span="2">
                  <Button
                    text={t`Search`}
                    size="medium"
                    type="submit"
                    onClick={search}
                  />
                </Grid.Column>
              </>
            ) : (
              !isFilePresent && (
                <>
                  <Flex marginLeft={1}>
                    <P shade={1} marginBottom={2}>
                      <Trans>
                        Upload a CSV file to configure multiple locations
                        automatically. Download this integration’s{' '}
                        <a
                          onClick={handleDownloadTemplate}
                          href="/"
                          target="_blank"
                          download="download-csv-template.csv"
                          rel="noopener noreferrer"
                        >
                          unique template
                        </a>
                        , populate the CSV template with the required details,
                        and upload the file to start the bulk location
                        configuration process.
                      </Trans>
                    </P>
                  </Flex>

                  {!_.isEmpty(invalidFileMessage) && (
                    <Grid.Column size="MD" span="12" marginBottom={1}>
                      <Alert intent="warning" title={invalidFileMessage} />
                    </Grid.Column>
                  )}
                  <Grid.Column size="MD" span="10">
                    <P shade={1} marginBottom={1}>
                      {t`To upload files from your workstation click BROWSE.`}
                    </P>
                  </Grid.Column>
                  <Grid.Column size="MD" span="12">
                    <Button
                      text={t`Browse`}
                      icon={iconx.Paperclip}
                      type="submit"
                      kind="secondary"
                      minWidth="100%"
                      size="small"
                      onClick={handleBrowseFile}
                    />
                  </Grid.Column>
                </>
              )
            )}
          </Grid.Row>

          {readyToSearch && !multipleLocationsMode ? (
            <WrapperWithLoading loading={loading}>
              <LocationsResult
                integration={integration}
                setSelectedLocationId={setSelectedLocationId}
                filterSearch={filterSearch}
                locations={locations}
                totalLocations={totalLocations}
                selectedLocationId={selectedLocationId}
              />
            </WrapperWithLoading>
          ) : (
            ''
          )}

          {isFilePresent && (
            <GreyishFlex>
              <P weight="semibold">{fileToUpload.name}</P>
              <Button
                icon={trashWithLegend}
                variant="ghost"
                onClick={handleRemovalOfSelectedFile}
                text=""
                aria-label={t`Remove file`}
              />
            </GreyishFlex>
          )}
        </Flex>
      </Modal>

      <Modal visible={addLocations} title={t`Adding Locations`}>
        <WrapperWithLoading
          loading={postingLocations}
          description={t`Adding locations`}
        >
          {false}
        </WrapperWithLoading>
      </Modal>

      <AddMultipleLocations
        visible={addLocations}
        isPosting={postingLocations}
        onClose={handleCloseModal}
        setRefresh={setRefresh}
      />

      <input
        type="file"
        data-testid="fileInput"
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".csv"
      />
    </>
  );
}

AddLocationModal.propTypes = {
  setVisibleModal: PropTypes.func.isRequired,
  visibleModal: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  integration: PropTypes.objectOf(PropTypes.any),
  setRefresh: PropTypes.func,
};
